import data from "./data";
import "./AccessGate.scss";

/**
 * Unlocks protected upload forms.
 *
 * The page contains only a neutral locked upload box (safe for page caches).
 * Each gallery has one shared record, so several boxes for the same gallery
 * make one bootstrap request, share one CSRF token, and unlock together.
 */

const records = new Map();

const recordFor = galleryId => {
    const id = String( galleryId );
    if ( !records.has( id ) ) {
        records.set( id, { id, gates: new Set(), state: "unknown", csrf: "", restNonce: "", formHtml: "", promise: null } );
    }
    return records.get( id );
};

const i18n = key => data.i18n[key] || "";

const call = async ( step, galleryId, body = null ) => {
    if ( typeof data.rest.access !== "string" ) throw { code: "network_error" };
    const url = new URL( data.rest.access, window.location.href );
    url.searchParams.set( "action", `fgfuu_access_${ step }` );
    url.searchParams.set( "gallery_id", galleryId );
    const init = { credentials: "same-origin", cache: "no-store" };
    if ( body ) {
        init.method = "POST";
        init.body = new URLSearchParams( { gallery_id: galleryId, ...body } );
    }
    let response;
    try {
        response = await fetch( url.toString(), init );
    } catch ( error ) {
        throw { code: "network_error" };
    }
    let result;
    try {
        result = await response.json();
    } catch ( error ) {
        throw { code: "bad_response", status: response.status };
    }
    if ( !response.ok ) throw { ...result, status: response.status };
    return result;
};

const apply = ( record, result ) => {
    if ( String( result.gallery_id ) !== record.id ) throw { code: "bad_response" };
    record.state = result.state;
    record.csrf = result.csrf || "";
    record.restNonce = result.rest_nonce || "";
    if ( result.state === "unlocked" && result.form_html ) record.formHtml = result.form_html;
    record.gates.forEach( gate => gate.receive( record ) );
    return result;
};

/**
 * Ask whether this browser is unlocked; also refreshes the CSRF token.
 */
export const bootstrap = record => {
    if ( !record.promise ) {
        record.promise = call( "bootstrap", record.id )
            .then( result => apply( record, result ) )
            .finally( () => { record.promise = null; } );
    }
    return record.promise;
};

const attempt = async ( record, step, secret, retried = false ) => {
    if ( !record.csrf ) await bootstrap( record );
    try {
        const result = await call( step, record.id, { csrf: record.csrf, secret } );
        if ( result.redirect ) {
            window.location.replace( result.redirect );
            return result;
        }
        return apply( record, result );
    } catch ( error ) {
        // The CSRF cookie can expire between page load and unlock: refresh once.
        if ( error?.code === "upload_csrf_failed" && !retried ) {
            await bootstrap( record );
            return attempt( record, step, secret, true );
        }
        throw error;
    }
};

export const messageFor = error => {
    switch ( error?.code ) {
        case "invalid_upload_code": return i18n( "accessWrongCode" );
        case "invalid_upload_link": return i18n( "accessLinkUnavailable" );
        case "access_throttled": return i18n( "accessThrottled" ).replace( "%d", String( Number( error.retry_after ) || 60 ) );
        case "network_error": return i18n( "accessNetworkError" );
        case "bad_response": return i18n( "accessUnavailable" );
        default: return error?.message || i18n( "accessUnavailable" );
    }
};

/**
 * Markup for the code controls, matching the server-rendered locked box.
 */
const controlsMarkup = id => `
    <label for="${ id }"></label>
    <span class="fg-feu-access-row">
        <span class="fg-feu-access-input">
            <input id="${ id }" type="password" autocomplete="off" autocapitalize="off" autocorrect="off" spellcheck="false" enterkeyhint="go" aria-describedby="${ id }-message"/>
            <button type="button" class="fg-feu-access-toggle" aria-pressed="false"></button>
        </span>
        <button type="button" class="fg-feu-access-unlock"></button>
    </span>
    <small id="${ id }-message" class="fg-feu-access-message" role="status" aria-live="polite"></small>`;

let controlsId = 0;

/**
 * Wire up a code input, Show/Hide toggle, Unlock button, and message.
 */
class CodeControls {
    constructor( container, onSubmit ) {
        this.container = container;
        this.input = container.querySelector( "input" );
        this.toggle = container.querySelector( ".fg-feu-access-toggle" );
        this.button = container.querySelector( ".fg-feu-access-unlock" );
        this.message = container.querySelector( ".fg-feu-access-message" );
        this.onSubmit = onSubmit;
        this.waitTimer = null;

        this.toggle.textContent = i18n( "accessShow" );
        this.button.textContent = i18n( "accessUnlock" );
        this.message.replaceChildren();
        this.toggle.addEventListener( "click", () => {
            const show = this.input.type === "password";
            this.input.type = show ? "text" : "password";
            this.toggle.textContent = show ? i18n( "accessHide" ) : i18n( "accessShow" );
            this.toggle.setAttribute( "aria-pressed", show ? "true" : "false" );
            this.input.focus();
        } );
        this.button.addEventListener( "click", () => this.submit() );
        this.input.addEventListener( "keydown", event => {
            if ( event.key === "Enter" ) {
                event.preventDefault();
                this.submit();
            }
        } );
        this.input.addEventListener( "input", () => {
            this.input.removeAttribute( "aria-invalid" );
            if ( !this.waitTimer ) this.say( "" );
        } );
    }

    say( text, isError = false ) {
        this.message.textContent = text;
        this.message.classList.toggle( "is-error", isError && text !== "" );
    }

    busy( isBusy, label = i18n( "accessUnlocking" ) ) {
        this.button.disabled = isBusy;
        this.button.textContent = isBusy ? label : i18n( "accessUnlock" );
    }

    async submit() {
        if ( this.button.disabled ) return;
        if ( this.input.value.trim() === "" ) {
            this.say( i18n( "accessEnterCode" ), true );
            this.input.focus();
            return;
        }
        this.busy( true );
        this.say( "" );
        try {
            await this.onSubmit( this.input.value );
            this.input.value = "";
        } catch ( error ) {
            this.fail( error );
        } finally {
            if ( !this.waitTimer ) this.busy( false );
        }
    }

    fail( error ) {
        this.say( messageFor( error ), true );
        if ( error?.code === "invalid_upload_code" ) {
            this.input.setAttribute( "aria-invalid", "true" );
            this.input.select();
        }
        if ( error?.code === "access_throttled" ) {
            this.busy( true, i18n( "accessUnlock" ) );
            clearTimeout( this.waitTimer );
            this.waitTimer = setTimeout( () => {
                this.waitTimer = null;
                this.busy( false );
                this.say( "" );
            }, ( Number( error.retry_after ) || 60 ) * 1000 );
        }
        this.input.focus();
    }
}

export default class AccessGate {
    /**
     * @param {HTMLElement} shell The locked upload box.
     * @param {(form: HTMLElement, gate: AccessGate) => object} initForm
     */
    constructor( shell, initForm ) {
        this.shell = shell;
        this.initForm = initForm;
        this.form = null;
        this.record = recordFor( shell.dataset.galleryId );
        this.record.gates.add( this );
        this.focusOnUnlock = false;
        this.controls = new CodeControls( shell, secret => {
            this.focusOnUnlock = true;
            return attempt( this.record, "unlock", secret );
        } );

        const key = window.FGFUU_LINK_KEY;
        if ( key ) {
            // Set by the share-link landing page; use it once.
            delete window.FGFUU_LINK_KEY;
            this.openLink( key );
        } else if ( this.record.state === "unlocked" ) {
            this.receive( this.record );
        } else {
            bootstrap( this.record ).catch( error => this.controls.say( messageFor( error ), true ) );
        }
    }

    async openLink( key ) {
        this.controls.busy( true, i18n( "accessChecking" ) );
        this.controls.say( i18n( "accessChecking" ) );
        this.focusOnUnlock = true;
        try {
            const result = await attempt( this.record, "exchange", key );
            if ( !result.redirect && this.record.state !== "unlocked" ) {
                // The link only opens this page; the code is still needed.
                this.controls.busy( false );
                this.controls.say( "" );
                this.controls.input.focus();
            }
        } catch ( error ) {
            this.controls.busy( false );
            this.controls.fail( error );
            // Rate limits are shared by everyone on the same network: keep the
            // link and try it again once the wait is over.
            if ( error?.code === "access_throttled" ) {
                setTimeout( () => this.openLink( key ), ( ( Number( error.retry_after ) || 60 ) + 1 ) * 1000 );
            }
        }
    }

    /**
     * Called whenever the gallery's shared record changes.
     */
    receive( record ) {
        if ( record.state !== "unlocked" || !record.formHtml || this.form ) return;
        const template = document.createElement( "template" );
        template.innerHTML = record.formHtml;
        const element = template.content.querySelector( ".fg-feu-form" );
        if ( !element ) return;
        this.shell.replaceWith( element );
        this.form = this.initForm( element, this );
        const zone = element.querySelector( ".fg-feu-drop-zone" );
        if ( zone ) {
            // Keyboard access to the hidden file input.
            zone.tabIndex = 0;
            zone.setAttribute( "role", "button" );
            zone.addEventListener( "keydown", event => {
                if ( event.key === "Enter" || event.key === " " ) {
                    event.preventDefault();
                    element.querySelector( "input[type=file]" )?.click();
                }
            } );
            if ( this.focusOnUnlock ) zone.focus();
        }
    }

    /**
     * Credentials for an upload request.
     */
    async credentials( refresh = false ) {
        if ( refresh || !this.record.csrf ) await bootstrap( this.record );
        return { csrf: this.record.csrf, nonce: this.record.restNonce };
    }

    /**
     * Access ended while files were selected: ask for the code inside the
     * dialog, keep the selection, then send the upload again.
     */
    requireAccess( dialog ) {
        this.record.state = "locked";
        dialog.hideLoader();
        dialog.buttonUpload.disabled = true;
        const panel = document.createElement( "div" );
        panel.className = "fg-feu-access-inline";
        const id = `fg-feu-access-inline-${ ++controlsId }`;
        panel.innerHTML = controlsMarkup( id );
        panel.querySelector( "label" ).textContent = i18n( "accessExpired" );
        dialog.body.prepend( panel );
        const controls = new CodeControls( panel, async secret => {
            await attempt( this.record, "unlock", secret );
            panel.remove();
            dialog.buttonUpload.disabled = false;
            if ( dialog.element.isConnected ) dialog.onUploadClicked( { preventDefault() {} } );
        } );
        controls.input.focus();
    }
}
